import { Box } from '@mui/material';
import ClientsList from 'components/featured/Client/ClientsList';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomAlertContain from 'components/shared/CustomAlert/CustomAlertContain';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomHeadline from 'components/shared/CustomHeadline/CustomHeadline';
import CustomTab from 'components/shared/CustomTab/CustomTab';
import { LayoutSwitcher } from 'components/shared/LayoutSwitcher';
import {
  ClientType,
  ClientV2TypeOption,
  FlatClientListResponse,
  GroupedClientListResponse,
} from 'model/client.model';
import { LayoutType } from 'model/deals.model';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientService } from 'service/client.service';
import { DashboardService } from 'service/dashboard.service';
import RoutePaths from 'routes/RoutePaths';
import zeplinColor from 'theme/Color';
import LinkingForm from './ClientForm/Forms/LinkingForm';
import style from './Client.module.css';

type SportsProps = {
  link: '';
  title: '';
  source: '';
};

const formatCategoryFromClientType = (value?: string) => {
  if (!value) return null;

  const normalized = value.toLowerCase();
  if (normalized === 'talent') return 'Talent';
  if (normalized === 'athlete') return 'Athlete';
  if (normalized === 'business_development') return 'Business_Development';

  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('_');
};

const Client = () => {
  const navigate = useNavigate();
  const new_client = sessionStorage.getItem('new-client') || '';
  const platform_created = sessionStorage.getItem('platform') || '';
  const link_successful = sessionStorage.getItem('link-successful') === 'true';

  const currentClientsLayout =
    (sessionStorage.getItem('currentClientsLayout') as LayoutType) || LayoutType.KANBAN;
  const [layout, setLayout] = useState<LayoutType>(currentClientsLayout);

  const handleLayoutChange = (_: SyntheticEvent, newValue: LayoutType) => {
    sessionStorage.setItem('currentClientsLayout', newValue);
    setLayout(newValue);
    setCurrentPage(0);
    setPageWindowStart(0);
    setInfinitePage(2);
    setHasMoreInfinite(false);
  };

  const [alert, setAlert] = React.useState('');
  const [value, setValue] = React.useState(0);
  const [groupClientSelected, setGroupClientSelected] = useState(false);

  const [linkModal, setLinkModal] = useState(false);
  const [clientTypes, setClientTypes] = useState<ClientV2TypeOption[]>([{ name: 'All' }]);

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageWindowStart, setPageWindowStart] = useState(0);

  const [infinitePage, setInfinitePage] = useState(2);
  const [hasMoreInfinite, setHasMoreInfinite] = useState(false);
  const [infiniteLoading, setInfiniteLoading] = useState(false);

  const [sortBy, setSortBy] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<('asc' | 'desc')[]>([]);

  const handleSort = (field: string, order: 'asc' | 'desc') => {
    setSortBy([field]);
    setSortOrder([order]);
    setCurrentPage(0);
    setPageWindowStart(0);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setCurrentPage(0);
    setPageWindowStart(0);
    setInfinitePage(2);
    setHasMoreInfinite(false);
  };
  const [data, setData] = useState<any>({
    clients: [
      {
        Name: '',
        AccountId: '',
        closedDealAmount: null,
        openDealAmount: null,
        category: null,
      },
    ],
  });
  const [groupedData, setGroupedData] = useState<GroupedClientListResponse | null>(null);

  const [loading, setLoading] = useState(false);
  const getSelectedClientType = React.useCallback(() => {
    const selectedType = clientTypes[value];
    return selectedType?.value || undefined;
  }, [clientTypes, value]);

  const getClientTypes = React.useCallback(async () => {
    try {
      const response = await ClientService.getClientTypesV2();
      const types =
        Array.isArray(response?.types) && response.types.length
          ? response.types
          : [{ name: 'All' }];
      setClientTypes(types);
    } catch (err) {
      console.error(err);
      setClientTypes([{ name: 'All' }]);
    }
  }, []);

  const mapFlatClients = React.useCallback((response: FlatClientListResponse, append = false) => {
    const sourceItems =
      (Array.isArray(response?.clients) && response.clients) ||
      (Array.isArray(response?.items) && response.items) ||
      (Array.isArray(response?.data) && response.data) ||
      [];

    const mappedClients: ClientType[] = sourceItems
      .map((item: any) => {
        const openDealAmount = item?.openDealAmount ?? null;
        const closedDealAmount = item?.closedDealAmount ?? null;
        return {
          Name: item?.name || item?.Name || 'N/A',
          Id: item?.clientId || item?.Id || item?.AccountId || item?.name || 'unknown-client',
          AccountId:
            item?.clientId || item?.AccountId || item?.Id || item?.name || 'unknown-client',
          closedDealAmount:
            closedDealAmount !== null && closedDealAmount !== undefined
              ? String(closedDealAmount)
              : null,
          openDealAmount:
            openDealAmount !== null && openDealAmount !== undefined ? String(openDealAmount) : null,
          category: item?.category || formatCategoryFromClientType(item?.clientType),
          avatar: item?.profileImageUrl || item?.avatar || null,
          healthScore: item?.healthScore ?? null,
          status: item?.healthStatus || item?.status || null,
          league:
            item?.league ||
            (Array.isArray(item?.classification) ? item.classification[0] : item?.classification) ||
            null,
          ttmRevenue: item?.ttmRevenue ?? null,
          activeDealCount: item?.activeDealCount ?? null,
          ownerName: item?.ownerName || null,
          primaryMetricLabel: 'TTM Revenue',
          primaryMetricValue: item?.ttmRevenue ?? null,
          secondaryMetricLabel: 'Deals',
          secondaryMetricValue: item?.activeDealCount ?? null,
          tertiaryMetricLabel: 'Owner',
          tertiaryMetricValue: item?.ownerName || 'N/A',
        };
      })
      .sort((a: ClientType, b: ClientType) => (a?.Name || '').localeCompare(b?.Name || ''));

    if (append) {
      setData((prev: any) => ({ clients: [...(prev.clients || []), ...mappedClients] }));
    } else {
      setData({ clients: mappedClients });
    }
  }, []);

  const fetchClients = React.useCallback(async () => {
    setLoading(true);

    const isKanban = layout === LayoutType.KANBAN;
    const page = isKanban ? 1 : currentPage + 1;
    const limit = isKanban ? 50 : rowsPerPage;

    try {
      const sortString =
        sortBy.length > 0 ? sortBy.map((f, i) => `${f}:${sortOrder[i] || 'asc'}`).join(',') : '';

      const response = await ClientService.getClientListV2({
        view: groupClientSelected ? 'grouped' : 'flat',
        clientType: getSelectedClientType(),
        search: '',
        page,
        limit,
        ...(sortString ? { sort: sortString } : {}),
      });

      const total = response?.total ?? response?.totalClients ?? 0;
      const pages = response?.totalPages ?? Math.ceil(total / limit);
      setTotalCount(total);
      setTotalPages(pages);

      if (isKanban) {
        setInfinitePage(2);
        setHasMoreInfinite(pages > 1);
      }

      if (groupClientSelected) {
        setGroupedData({
          view: response?.view || 'grouped',
          enterprises: response?.enterprises || [],
          standalone: response?.standalone || [],
          total: response?.total,
          page: response?.page,
          limit: response?.limit,
          totalPages: response?.totalPages,
          totalClients: response?.totalClients,
          types: response?.types,
        });
      } else {
        setGroupedData(null);
        mapFlatClients(response as FlatClientListResponse);
      }
    } catch (err: any) {
      console.error(err);
      setTotalCount(0);
      setTotalPages(0);
      setHasMoreInfinite(false);
      if (groupClientSelected) {
        setGroupedData({ view: 'grouped', enterprises: [], standalone: [] });
      } else {
        setData({ clients: [] });
      }
    } finally {
      setLoading(false);
    }
  }, [
    getSelectedClientType,
    groupClientSelected,
    mapFlatClients,
    currentPage,
    rowsPerPage,
    layout,
    sortBy,
    sortOrder,
  ]);

  const refreshClients = React.useCallback(async () => {
    await fetchClients();
  }, [fetchClients]);

  const fetchMoreClients = React.useCallback(async () => {
    if (infiniteLoading || !hasMoreInfinite) return;
    setInfiniteLoading(true);
    try {
      const response = await ClientService.getClientListV2({
        view: groupClientSelected ? 'grouped' : 'flat',
        clientType: getSelectedClientType(),
        search: '',
        page: infinitePage,
        limit: 50,
      });
      const total = response?.total ?? response?.totalClients ?? 0;
      const pages = response?.totalPages ?? Math.ceil(total / 50);
      setHasMoreInfinite(infinitePage < pages);
      setInfinitePage((prev) => prev + 1);
      if (groupClientSelected) {
        setGroupedData((prev) => ({
          ...prev!,
          view: prev?.view || 'grouped',
          enterprises: [...(prev?.enterprises || []), ...(response?.enterprises || [])],
          standalone: [...(prev?.standalone || []), ...(response?.standalone || [])],
        }));
      } else {
        mapFlatClients(response as FlatClientListResponse, true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setInfiniteLoading(false);
    }
  }, [
    infiniteLoading,
    hasMoreInfinite,
    infinitePage,
    groupClientSelected,
    getSelectedClientType,
    mapFlatClients,
  ]);

  const getAllClient = async () => {
    await refreshClients();
  };

  const handleNewClientLink = () => {
    setAlert('');
    setLinkModal(true);
  };

  useEffect(() => {
    getClientTypes();
  }, [getClientTypes]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const ClientTabs = clientTypes.map((clientType, index) => ({
    label: `${clientType.name}  (${clientType.count ?? '-'})`,
    id: index + 1,
    isHidden: value !== index,
    Component: (
      <Box>
        <ClientsList
          layout={layout}
          category={'all'}
          loading={loading}
          data={data}
          groupedSelected={groupClientSelected}
          groupedData={groupedData}
          pagination={{
            currentPage,
            rowsPerPage,
            totalCount,
            totalPages,
            pageWindowStart,
            setCurrentPage: (val) => {
              setCurrentPage(val);
            },
            setRowsPerPage: (val) => {
              setRowsPerPage(val);
              setCurrentPage(0);
              setPageWindowStart(0);
            },
            setPageWindowStart,
          }}
          infiniteScroll={{
            hasMore: hasMoreInfinite,
            fetchMore: fetchMoreClients,
            loading: infiniteLoading,
            totalCount,
          }}
          onSort={handleSort}
        />
      </Box>
    ),
  }));

  const [sportsData, setSportsData] = React.useState<SportsProps[]>([
    {
      link: '',
      title: '',
      source: '',
    },
  ]);

  const getSportNews = async () => {
    try {
      const res: { latestNews: SportsProps[] } = await DashboardService.getSportNews();
      console.log(res);
      setSportsData(res?.latestNews);
    } catch (err) {
      console.log(err, 'err');
    }
  };

  const handleClientCreateClick = (_event: React.MouseEvent<HTMLDivElement>) => {
    navigate(`${RoutePaths.Client}/new`);
  };
  useEffect(() => {
    getSportNews();
    new_client && setAlert('Client added');
    new_client && setTimeout(handleNewClientLink, 1000);
    link_successful && setAlert('Integrations connected');
    link_successful &&
      setTimeout(() => {
        setAlert('');
        sessionStorage.removeItem('link-successful');
      }, 2000);
  }, [new_client]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box>
        <CustomHeadline sportsData={sportsData} />
      </Box>
      <Box
        sx={{
          padding: '24px 1.5rem 0rem 1.5rem',
          position: 'relative',
          overflow: 'auto',
        }}
      >
        {' '}
        <CustomTab tabs={ClientTabs} handleChange={handleChange} value={value} />
        <Box
          sx={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
          }}
        >
          <LayoutSwitcher layout={layout} onLayoutChange={handleLayoutChange} />

          <Box
            onClick={() => {
              setGroupClientSelected((prev) => !prev);
              setCurrentPage(0);
              setPageWindowStart(0);
              setInfinitePage(2);
              setHasMoreInfinite(false);
            }}
            sx={{
              width: '88px',
              height: '48px',
              borderRadius: '50px',
              padding: '6px 6px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              backgroundColor: groupClientSelected ? zeplinColor.BasicBlack : zeplinColor.Neutral1,
              border: groupClientSelected ? 'none' : `1px solid ${zeplinColor.Neutral6}`,
            }}
          >
            <img
              src={
                groupClientSelected
                  ? '/assets/group-client-selected.svg'
                  : '/assets/group-client.svg'
              }
              alt='group-client'
              style={{ width: 20, height: 20 }}
            />
          </Box>

          <CustomButton
            disabled={false}
            onClick={handleClientCreateClick}
            variant='contained'
            color={'primary'}
            buttonVariant='responsiveButtonLarge'
            style={{
              width: '100%',
              borderRadius: { xs: '10px', xl: '14px' },
              height: { xl: 48 },
              padding: { xs: '10px 12px', xl: '14px 16px' },
              '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
            }}
            textStyle={{ color: zeplinColor.Neutral1 }}
            text={'New Client'}
            icon={<img src={`/assets/add-white.svg`} className={style['add-icon']} />}
            iconPosition='start'
          />
        </Box>
      </Box>

      <LinkingForm
        modalOpen={linkModal}
        clientId={new_client}
        platform_created={platform_created}
        setLinkModal={setLinkModal}
        getAllClient={getAllClient}
      />

      <CustomAlert
        open={alert ? true : false}
        handleClose={() => {
          setAlert('');
        }}
        message={
          <CustomAlertContain
            alertIcon={
              <img src='/assets/checkmark-green.png' width={24} height={24} alt='green checkmark' />
            }
            alert={alert ? `${alert}` : ''}
          />
        }
        error={false}
      />
    </Box>
  );
};

export default Client;
